import { PLAYBACK_SEEK_STEPS } from '../lib/playbackSeek'
import { useI18n } from '../i18n/useI18n'

interface Props {
  onSeek: (seconds: number) => void
  onPlay?: () => void | Promise<void>
  onStop?: () => void
  disabled?: boolean
  playDisabled?: boolean
  stopDisabled?: boolean
  playing?: boolean
  className?: string
  playLabel?: string
  stopLabel?: string
}

interface DirectionIconProps {
  direction: 'left' | 'right'
  count: 1 | 2 | 3
}

/**
 * 초 단위 숫자를 화면에 쓰지 않고도 이동량을 구분할 수 있도록
 * 1초는 삼각형 1개, 5초는 2개, 10초는 3개로 표시합니다.
 */
function DirectionIcon({ direction, count }: DirectionIconProps) {
  const triangleWidth = 10
  const gap = 1.25
  const totalWidth = triangleWidth + (count - 1) * (triangleWidth - gap)
  const start = (36 - totalWidth) / 2

  return (
    <svg viewBox="0 0 36 24" aria-hidden="true" focusable="false">
      {Array.from({ length: count }, (_, index) => {
        const x = start + index * (triangleWidth - gap)
        const points = direction === 'left'
          ? `${x + triangleWidth},2 ${x},12 ${x + triangleWidth},22`
          : `${x},2 ${x + triangleWidth},12 ${x},22`
        return <polygon key={index} points={points} fill="currentColor" />
      })}
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 36 24" aria-hidden="true" focusable="false">
      <polygon points="9,2 29,12 9,22" fill="currentColor" />
    </svg>
  )
}

function StopIcon() {
  return (
    <svg viewBox="0 0 36 24" aria-hidden="true" focusable="false">
      <rect x="8" y="2" width="20" height="20" rx="1.8" fill="currentColor" />
    </svg>
  )
}

function seekIconCount(seconds: number): 1 | 2 | 3 {
  if (Math.abs(seconds) === 10) return 3
  if (Math.abs(seconds) === 5) return 2
  return 1
}

/**
 * 유튜브와 파일 음원에서 공통으로 사용하는 그림 아이콘 재생 조작부입니다.
 * 왼쪽부터 10초·5초·1초 뒤로, 재생, 정지, 1초·5초·10초 앞으로 이동합니다.
 */
export function PlaybackSeekControls({
  onSeek,
  onPlay,
  onStop,
  disabled = false,
  playDisabled = disabled,
  stopDisabled = disabled,
  playing = false,
  className = '',
  playLabel,
  stopLabel,
}: Props) {
  const { t } = useI18n()
  const resolvedPlayLabel = playLabel ?? t('playback.play')
  const resolvedStopLabel = stopLabel ?? t('playback.stop')
  const backwardSteps = PLAYBACK_SEEK_STEPS.filter((seconds) => seconds < 0)
  const forwardSteps = PLAYBACK_SEEK_STEPS.filter((seconds) => seconds > 0)

  return (
    <div className={`playback-seek-controls ${className}`.trim()} aria-label={t('playback.controls')}>
      {backwardSteps.map((seconds) => (
        <button
          key={seconds}
          type="button"
          className="button secondary playback-icon-button"
          disabled={disabled}
          title={t('playback.seekBackward', { seconds: Math.abs(seconds) })}
          aria-label={t('playback.seekBackward', { seconds: Math.abs(seconds) })}
          onClick={() => onSeek(seconds)}
        >
          <DirectionIcon direction="left" count={seekIconCount(seconds)} />
        </button>
      ))}

      <button
        type="button"
        className={`button primary playback-icon-button ${playing ? 'active' : ''}`.trim()}
        disabled={playDisabled || !onPlay}
        title={resolvedPlayLabel}
        aria-label={resolvedPlayLabel}
        aria-pressed={playing}
        onClick={() => { if (onPlay) void onPlay() }}
      >
        <PlayIcon />
      </button>

      <button
        type="button"
        className="button secondary playback-icon-button"
        disabled={stopDisabled || !onStop}
        title={resolvedStopLabel}
        aria-label={resolvedStopLabel}
        onClick={() => onStop?.()}
      >
        <StopIcon />
      </button>

      {forwardSteps.map((seconds) => (
        <button
          key={seconds}
          type="button"
          className="button secondary playback-icon-button"
          disabled={disabled}
          title={t('playback.seekForward', { seconds: Math.abs(seconds) })}
          aria-label={t('playback.seekForward', { seconds: Math.abs(seconds) })}
          onClick={() => onSeek(seconds)}
        >
          <DirectionIcon direction="right" count={seekIconCount(seconds)} />
        </button>
      ))}
    </div>
  )
}
